import { useCallback, useEffect, useRef, useState } from 'react';

import { useBridgeApi } from '@/api/BridgeApi.ts';
import { useBridgewayApi } from '@/api/BridgewayApi.ts';
import { useUserId } from '@/auth/UserIdContext.tsx';
import { logger } from '@/logger';

import type { Chat } from '../../../../bridge/types/state.ts';
import { isUnnamedChatTitle } from '../../../../bridge/utils/chatTitle.ts';

export type ChatThreadMeta = {
	id: string;
	title: string;
	created_at: string;
	updated_at: string;
	is_archived: boolean;
	is_unread: boolean;
	is_setup_only?: boolean;
};

export type CosmosChatThread = ChatThreadMeta & {
	user_id: string;
	type?: string;
	// Since ADO-2149 the list route PROJECTS, and `messages` is not in the
	// projection (services/py/api/routes/chat_threads.py). The sidebar row therefore
	// carries no transcript at all, and the `messages.length` fallback below can
	// only fire for a thread fetched by id.
	//
	// So `message_count` on a list row has to be right at the source. The store
	// writes it on every head, and the projection recovers it with
	// `ARRAY_LENGTH(c.messages)` for a row no store write produced. That recovery
	// tests IS_NULL as well as IS_DEFINED: a direct writer dumps the model, which
	// emits an explicit null rather than omitting the key, and an undefined-only
	// coalesce let a real reconnect thread read as empty and vanish from here.
	//
	// A thread past the Cosmos 2 MB limit keeps only a recent PREVIEW in
	// `messages`, with the rest in page documents, so `message_count` is the
	// TOTAL and `messages.length` is not.
	message_count?: number;
	first_message_type?: string;
	messages?: ReadonlyArray<unknown>;
	linked_item?: { type?: string; id?: string } | null;
};

// SMS chat threads are tagged is_unlisted=true (bridge state) and type="sms"
// (persisted to Cosmos, which has no is_unlisted column). Both markers are
// checked so the filter survives independently of which source the row came
// from.
const isHiddenBridgeChat = (chat: Chat): boolean =>
	chat.is_unlisted === true || chat.type === 'sms';

// A chat with no real name gets an empty title, which the sidebar renders as
// "New chat". Some older rows still hold a literal "Untitled" label; blank it
// here so it reads the same as any other unnamed chat until it gets a real one.
// The `?? ''` never runs — isUnnamedChatTitle is true for null and undefined —
// but the compiler can't tell that, since it isn't a type predicate.
const displayTitle = (title: string | null | undefined): string =>
	isUnnamedChatTitle(title) ? '' : (title ?? '');

const isHiddenCosmosThread = (thread: CosmosChatThread): boolean =>
	thread.type === 'sms';

// Prefer the stored total. A paged thread keeps only a preview in `messages`, so
// `messages.length` under-reports it.
const cosmosMessageCount = (thread: CosmosChatThread): number =>
	thread.message_count ?? thread.messages?.length ?? 0;

// A row counts as empty only when the stored count says so AND it carries no
// message. A writer that stores a count of 0 beside a real message would
// otherwise hide a real thread from the sidebar.
const isEmptyCosmosThread = (thread: CosmosChatThread): boolean =>
	cosmosMessageCount(thread) === 0 && (thread.messages?.length ?? 0) === 0;

const isEmptyBridgeChat = (chat: Chat): boolean => chat.messages.length === 0;

const isProcessSetupOnlyMessages = (
	messages: ReadonlyArray<unknown> | undefined,
): boolean => {
	if ((messages?.length ?? 0) !== 1) return false;
	const [message] = messages ?? [];
	return (
		typeof message === 'object' &&
		message !== null &&
		'type' in message &&
		message.type === 'process_setup_panel'
	);
};

const isSetupOnlyBridgeChat = (chat: Chat): boolean =>
	chat.linked_item?.type === 'process' &&
	isProcessSetupOnlyMessages(chat.messages);

const isSetupOnlyCosmosThread = (thread: CosmosChatThread): boolean => {
	if (thread.linked_item?.type !== 'process') return false;
	// The stored count and first-message type answer "one message, and it is the
	// setup card" without reading the transcript. A paged thread keeps only a
	// preview in `messages`, so that transcript test cannot be trusted there.
	//
	// The gate is `first_message_type`, not `message_count`. A row can carry a
	// count and no type, and gating on the count would then compare the type
	// against undefined and answer false for a thread this used to hide.
	if (thread.first_message_type !== undefined) {
		return (
			cosmosMessageCount(thread) === 1 &&
			thread.first_message_type === 'process_setup_panel'
		);
	}
	return isProcessSetupOnlyMessages(thread.messages);
};

// Pure sort: merge bridge chats (live, source-of-truth for active sessions)
// with cosmos-only threads (persisted history) and order newest-first by
// updated_at. Extracted for direct unit testing.
//
// Coerces missing/invalid updated_at to 0 so legacy cosmos rows without a
// timestamp sort to the bottom instead of poisoning the comparator with NaN.
const toMillis = (iso: string | undefined): number => {
	const t = new Date(iso ?? 0).getTime();
	return Number.isNaN(t) ? 0 : t;
};

// Pre-deploy chats in Redis lack updated_at. Fall back to the last
// message timestamp so they sort by recency instead of sinking to the
// bottom on cold load. Self-heals once the chat is touched and
// appendChatMessage stamps a real updated_at.
const bridgeUpdatedAt = (chat: Chat): string | undefined => {
	if (chat.updated_at) return chat.updated_at;
	const msgs = chat.messages;
	for (let i = msgs.length - 1; i >= 0; i--) {
		const message = msgs[i];
		const ts = 'timestamp' in message ? message.timestamp : undefined;
		if (ts) return ts;
	}
	return undefined;
};

export const sortThreads = (
	bridgeChats: Record<string, Chat>,
	cosmosThreadsById: Record<string, ChatThreadMeta>,
): ChatThreadMeta[] => {
	const bridgeThreads = Object.fromEntries(
		Object.entries(bridgeChats).flatMap(([id, chat]) => {
			if (isHiddenBridgeChat(chat)) return [];
			if (chat.is_loading === true && cosmosThreadsById[id]) return [];
			if (isEmptyBridgeChat(chat)) return [];

			const updatedAt = bridgeUpdatedAt(chat) ?? '';
			return [
				[
					id,
					{
						id,
						title: displayTitle(chat.title),
						created_at: chat.created_at ?? updatedAt,
						updated_at: updatedAt,
						is_archived: false,
						is_unread: chat.is_unread ?? false,
						is_setup_only: isSetupOnlyBridgeChat(chat),
					},
				],
			];
		}),
	) as Record<string, ChatThreadMeta>;

	return Object.values({ ...cosmosThreadsById, ...bridgeThreads }).sort(
		(a, b) => toMillis(b.updated_at) - toMillis(a.updated_at),
	);
};

export const buildCosmosThreadsById = (
	cosmosThreads: CosmosChatThread[],
): Record<string, ChatThreadMeta> =>
	Object.fromEntries(
		cosmosThreads
			.filter(
				(t) =>
					!t.is_archived && !isHiddenCosmosThread(t) && !isEmptyCosmosThread(t),
			)
			.map((thread) => [
				thread.id,
				{
					id: thread.id,
					title: displayTitle(thread.title),
					created_at: thread.created_at,
					updated_at: thread.updated_at,
					is_archived: thread.is_archived,
					is_unread: thread.is_unread ?? false,
					is_setup_only: isSetupOnlyCosmosThread(thread),
				},
			]),
	);

export function useChatThreads() {
	const bridgewayApi = useBridgewayApi();
	const bridgeApi = useBridgeApi();
	const userId = useUserId();
	const [cosmosThreadsById, setCosmosThreadsById] = useState<
		Record<string, ChatThreadMeta>
	>({});
	const [loaded, setLoaded] = useState(false);
	const [loadFailed, setLoadFailed] = useState(false);

	// Each call bumps this and captures its own id. A response only writes state
	// if its id is still the latest, so a slow earlier request cannot clobber a
	// faster later one. Mirrors the guard in useActiveJobs.ts. Two reads are
	// genuinely concurrent here: the chat-change effect and the focus listener
	// below both call refresh(), and a reconnect thread created between the two
	// requests would disappear again when the older response landed last.
	const requestIdRef = useRef(0);

	const refresh = useCallback(async () => {
		if (!userId) return;
		const requestId = ++requestIdRef.current;
		try {
			const data = await bridgewayApi.get<CosmosChatThread[]>(
				`/api/user/${userId}/chat_threads/`,
			);
			if (requestId === requestIdRef.current) {
				setCosmosThreadsById(buildCosmosThreadsById(data));
				setLoadFailed(false);
			}
		} catch (error) {
			// The live chats still render, so this stays non-fatal — but it is
			// reported now rather than swallowed. A silent catch here showed a
			// short list that looked complete, and a broken stored-thread query
			// read as "this account has no history" for as long as it lasted.
			//
			// The failure banner is gated by the same staleness guard as the
			// success path: a slow, now-superseded request's failure must not
			// override a faster later request's success.
			if (requestId === requestIdRef.current) {
				setLoadFailed(true);
			}
			logger.error('Failed to load stored chat threads', error);
		} finally {
			// Unconditional: `loaded` only means a first attempt finished, so a
			// superseded request must still clear the skeleton.
			setLoaded(true);
		}
	}, [userId, bridgewayApi]);

	const currentChatId = bridgeApi.user_state?.current_chat_id ?? null;

	useEffect(() => {
		void refresh();
	}, [currentChatId, refresh]);

	// A thread the agent creates while this tab is closed, asleep, or in the
	// background reaches the sidebar only on the next read of the stored list.
	// Coming back to the tab is the moment the customer expects to see it.
	useEffect(() => {
		const onFocus = () => {
			void refresh();
		};
		window.addEventListener('focus', onFocus);
		return () => window.removeEventListener('focus', onFocus);
	}, [refresh]);

	// Not memoized: deltas mutate bridgeApi.user_state.chats in place, so its
	// reference is stable across updates. A useMemo keyed on the chats object
	// would never invalidate, leaving unread state stale until a full refresh.
	const threads = sortThreads(
		bridgeApi.user_state?.chats ?? {},
		cosmosThreadsById,
	);

	return { threads, refresh, loaded, loadFailed };
}
